import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import { trackAnalyticsEvent } from '../lib/analytics';

import {
  normalizeRef,
  normalizePhone,
  isValidEmailFormat,
  isValidPhoneFormat
} from './subscribeUtils';

const EMPTY_REFERENCES = [];

const CheckoutModal = ({ open, onClose, email, defaultReferences = EMPTY_REFERENCES, onSuccess, requireSubscriber = false }) => {
  const [form, setForm] = useState({
    email: email || '',
    fullName: '',
    phone: '',
    references: defaultReferences.join(', ')
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [redirecting, setRedirecting] = useState(false);

  const canonicalEmail = useMemo(() => (email || '').trim(), [email]);
  const canonicalRefs = useMemo(
    () => defaultReferences
      .map((item) => normalizeRef(item))
      .map((item) => item.replace(/^IRL/i, ''))
      .filter((item) => /^\d{8}$/.test(item)),
    [defaultReferences]
  );
  const lockEmail = requireSubscriber && !!canonicalEmail;
  const lockReferences = requireSubscriber && canonicalRefs.length > 0;

  useEffect(() => {
    if (!open) return;
    setForm((prev) => ({
      ...prev,
      email: lockEmail ? canonicalEmail : (email || prev.email),
      phone: prev.phone,
      references: lockReferences ? canonicalRefs.join(', ') : defaultReferences.join(', ')
    }));
  }, [open, email, defaultReferences, lockEmail, lockReferences, canonicalEmail, canonicalRefs]);

  useEffect(() => {
    if (!open) return;
    trackAnalyticsEvent('begin_checkout', {
      currency: 'INR',
      value: 349,
      items: [{ item_id: 'premium-monitoring-tools', item_name: 'Premium monitoring and tools', price: 349, quantity: 1 }]
    });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const closeModal = useCallback(() => {
    setLoading(false);
    setError(null);
    setRedirecting(false);
    if (typeof onClose === 'function') onClose();
  }, [onClose]);

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    if (lockEmail && name === 'email') return;
    if (lockReferences && name === 'references') return;
    setForm((prev) => ({
      ...prev,
      [name]: name === 'phone'
        ? value.slice(0, 16)
        : (type === 'checkbox' ? checked : value)
    }));
  };

  const expectedRefSet = useMemo(() => new Set(canonicalRefs), [canonicalRefs]);

  const parseReferences = () => {
    return form.references
      .split(',')
      .map((item) => normalizeRef(item))
      .filter(Boolean);
  };

  const handleCheckout = async (event) => {
    event.preventDefault();
    if (loading) return;
    setError(null);

    const trimmedEmail = lockEmail ? canonicalEmail : (form.email || '').trim();
    const normalizedPhone = normalizePhone(form.phone || '');
    const refs = lockReferences ? canonicalRefs : parseReferences();
    if (!trimmedEmail || !isValidEmailFormat(trimmedEmail)) {
      setError('Enter a valid email address.');
      return;
    }
    if (!normalizedPhone || !isValidPhoneFormat(normalizedPhone)) {
      setError('Enter a valid 10-digit Indian mobile number.');
      return;
    }
    if (!refs.length) {
      setError('Add at least one visa reference number.');
      return;
    }
    if (refs.some((ref) => /^IRL/i.test(ref))) {
      setError('Do not include IRL. Enter only the 8-digit number (e.g. 12345678).');
      return;
    }
    if (!refs.every((ref) => /^\d{8}$/.test(ref))) {
      setError('Visa reference number must be exactly 8 digits (numbers only, no IRL prefix).');
      return;
    }
    if (requireSubscriber) {
      if (!email) {
        setError('Create a free account before upgrading.');
        return;
      }
      if (trimmedEmail.toLowerCase() !== email.trim().toLowerCase()) {
        setError('Use the same email you verified during the free subscription.');
        return;
      }
      if (expectedRefSet.size > 0 && !refs.every((ref) => expectedRefSet.has(ref))) {
        setError('Your upgrade must use the visa reference number linked to your account.');
        return;
      }
    }
    setLoading(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/api/payments/payu/create`, {
        email: trimmedEmail,
        fullName: form.fullName.trim(),
        phone: normalizedPhone,
        references: refs,
        amount: 34900,
        currency: 'INR'
      });

      const data = response?.data;
      if (!data?.success || !data?.actionUrl || !data?.fields) {
        throw new Error(data?.error || 'Unable to create order');
      }

      const analyticsTransactionId = window.crypto?.randomUUID?.();
      trackAnalyticsEvent('checkout_order_created', { currency: 'INR', value: 349 });
      if (analyticsTransactionId) {
        try {
          sessionStorage.setItem('ivtAnalyticsCheckoutPending', analyticsTransactionId);
        } catch (_) {
          // Checkout must not depend on analytics storage.
        }
      }

      const formEl = document.createElement('form');
      formEl.method = 'POST';
      formEl.action = data.actionUrl;
      formEl.style.display = 'none';

      Object.entries(data.fields).forEach(([fieldName, fieldValue]) => {
        if (fieldValue === undefined || fieldValue === null) return;
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = fieldName;
        input.value = String(fieldValue);
        formEl.appendChild(input);
      });

      document.body.appendChild(formEl);
      setRedirecting(true);
      setTimeout(() => {
        try {
          formEl.submit();
        } finally {
          setTimeout(() => {
            if (formEl.parentNode) {
              formEl.parentNode.removeChild(formEl);
            }
          }, 2000);
        }
      }, 0);
      closeModal();
    } catch (checkoutError) {
      console.error('Checkout error', checkoutError);
      setError(checkoutError?.response?.data?.error || checkoutError?.message || 'Something went wrong. Please try again.');
      setLoading(false);
      setRedirecting(false);
    }
  };

  if (!open) return null;

  return (
    <div className="modal-backdrop" onClick={closeModal}>
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="checkout-title"
        aria-describedby="checkout-desc"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h2 id="checkout-title">Upgrade for ₹349</h2>
          <button className="btn-dismiss" onClick={closeModal} aria-label="Close checkout">×</button>
        </div>
        <div className="modal-body" id="checkout-desc">
          <p>One-time payment for unlimited visa status searches, instant email + web push notifications, and access to all 13 premium tools.</p>
          <form onSubmit={handleCheckout} className="checkout-form">
            <label className="form-field">
              <span>Full name</span>
              <input
                type="text"
                name="fullName"
                required
                value={form.fullName}
                onChange={handleChange}
                placeholder="Your full name"
                autoComplete="name"
              />
            </label>

            <label className="form-field">
              <span>Email</span>
              <input
                type="email"
                name="email"
                required
                value={form.email}
                onChange={handleChange}
                placeholder="you@example.com"
                autoComplete="email"
                disabled={lockEmail}
              />
              {lockEmail && <span className="form-hint">Email verified for this account. Contact support to change it.</span>}
            </label>

            <label className="form-field">
              <span>Mobile number (India, 10 digits)</span>
              <input
                type="tel"
                name="phone"
                required
                value={form.phone}
                onChange={handleChange}
                placeholder="9876543210"
                inputMode="numeric"
                maxLength={16}
                autoComplete="tel-national"
              />
            </label>

            <label className="form-field">
              <span>Visa reference number (8 digits only, no IRL prefix. e.g. 12345678)</span>
              <input
                type="text"
                name="references"
                value={form.references}
                onChange={handleChange}
                placeholder="12345678"
                inputMode="numeric"
                pattern="\d{8}"
                maxLength={8}
                disabled={lockReferences}
              />
              {lockReferences && <span className="form-hint">Reference number locked to your verified subscription.</span>}
            </label>

            {error && <div className="form-error" role="alert">{error}</div>}

            <p className="form-hint">
              This is a one-time, non-refundable service purchase. Please read
              our <a href="/refund" target="_blank" rel="noopener noreferrer">refund policy</a> before paying.
            </p>

            <div className="modal-actions">
              <button className="btn-primary" type="submit" disabled={loading || redirecting}>
                {redirecting ? 'Redirecting…' : (loading ? 'Processing…' : 'Proceed to Pay ₹349')}
              </button>
              <button className="btn-secondary" type="button" onClick={closeModal} disabled={loading}>Cancel</button>
            </div>

            <div className="checkout-trust-note">
              <span>🔒 Secure PayU checkout · Lifetime alerts + 13 tools · Instant activation</span>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CheckoutModal;
